/**
 * Instagram Reels admin page (instagram-reels plugin).
 *
 * Route: /projects/:projectId/instagram-reels
 *
 * The moderation queue + curation surface:
 *   - add any reel by URL (curated by you → approved immediately)
 *   - approve / reject visitor submissions (they always arrive Pending)
 *   - choose placements (home rail / product pages) and link products
 *   - reorder what the storefront shows
 *
 * Only APPROVED reels are ever served to the storefront — the public API
 * enforces that structurally, this page is where approval happens.
 */
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  Check,
  ExternalLink,
  Home,
  Instagram,
  Plus,
  ShoppingBag,
  Trash2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, apiHelpers, type InstagramReel } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { useConfirm } from '@/components/ui/confirm-provider';
import { MultiSelectPicker, type PickerItem } from '@/components/ui/multi-select-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

const STATUS_BADGE: Record<InstagramReel['status'], { label: string; className: string }> = {
  pending: { label: 'Pending review', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Approved', className: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Rejected', className: 'bg-red-100 text-red-700' },
};

export default function InstagramReelsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({ url: '', caption: '', credit: '' });
  const [draftPlacements, setDraftPlacements] = useState<Array<'home' | 'product'>>(['home']);
  const [draftProductIds, setDraftProductIds] = useState<string[]>([]);

  const listQuery = useQuery({
    queryKey: ['instagram-reels', projectId, statusFilter],
    queryFn: async () => {
      const res = await apiHelpers.listInstagramReels(projectId!, {
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
        limit: 100,
      });
      return (res.data.data || []) as InstagramReel[];
    },
    enabled: Boolean(projectId),
  });

  const statsQuery = useQuery({
    queryKey: ['instagram-reels-stats', projectId],
    queryFn: async () => {
      const res = await apiHelpers.getInstagramReelStats(projectId!);
      return res.data.data || { pending: 0, approved: 0, rejected: 0 };
    },
    enabled: Boolean(projectId),
  });

  // Product directory for the "link products" picker (name + SKU searchable).
  const productsQuery = useQuery({
    queryKey: ['instagram-reels-products', projectId],
    queryFn: async () => {
      const res = await api.get(`/projects/${projectId}/ecommerce/products`, {
        params: { limit: 100 },
      });
      const items = (res.data.data || []) as Array<{
        _id: string;
        name: string;
        sku?: string;
      }>;
      return items.map<PickerItem>((p) => ({
        value: p._id,
        label: p.name,
        secondary: p.sku,
      }));
    },
    enabled: Boolean(projectId),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['instagram-reels', projectId] });
    queryClient.invalidateQueries({ queryKey: ['instagram-reels-stats', projectId] });
  };

  const createMutation = useMutation({
    // Accepts several links at once (one per line / whitespace-separated) so
    // curating from Instagram's Tagged tab is one paste. Each link is its own
    // API call; partial failures are reported without losing the successes.
    mutationFn: async () => {
      const urls = draft.url
        .split(/\s+/)
        .map((u) => u.trim())
        .filter(Boolean);
      let added = 0;
      const failures: string[] = [];
      for (const url of urls) {
        try {
          await apiHelpers.createInstagramReel(projectId!, {
            url,
            caption: urls.length === 1 ? draft.caption.trim() || undefined : undefined,
            credit: draft.credit.trim() || undefined,
            placements: draftPlacements,
            productIds: draftProductIds,
          });
          added++;
        } catch (err: any) {
          failures.push(`${url.slice(-14)}: ${err?.response?.data?.error?.message || 'failed'}`);
        }
      }
      return { added, failures };
    },
    onSuccess: ({ added, failures }) => {
      if (added > 0) toast.success(`${added} reel${added === 1 ? '' : 's'} added and approved`);
      for (const f of failures) toast.error(f);
      if (failures.length === 0) {
        setAddOpen(false);
        setDraft({ url: '', caption: '', credit: '' });
        setDraftPlacements(['home']);
        setDraftProductIds([]);
      }
      invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message || 'Could not add those reels'),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Parameters<typeof apiHelpers.updateInstagramReel>[2];
    }) => apiHelpers.updateInstagramReel(projectId!, id, data),
    onSuccess: () => invalidate(),
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Update failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiHelpers.deleteInstagramReel(projectId!, id),
    onSuccess: () => {
      toast.success('Reel removed');
      invalidate();
    },
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Delete failed'),
  });

  const reorderMutation = useMutation({
    mutationFn: (ids: string[]) => apiHelpers.reorderInstagramReels(projectId!, ids),
    onSuccess: () => invalidate(),
    onError: (err: any) => toast.error(err?.response?.data?.error?.message || 'Reorder failed'),
  });

  const reels = listQuery.data || [];
  const stats = statsQuery.data;

  const move = (index: number, dir: -1 | 1) => {
    const next = reels.map((r) => r._id);
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    reorderMutation.mutate(next);
  };

  const togglePlacement = (reel: InstagramReel, placement: 'home' | 'product') => {
    const has = reel.placements.includes(placement);
    const placements = has
      ? reel.placements.filter((p) => p !== placement)
      : [...reel.placements, placement];
    updateMutation.mutate({ id: reel._id, data: { placements } });
  };

  const filters: Array<{ id: StatusFilter; label: string; count?: number }> = [
    { id: 'all', label: 'All' },
    { id: 'pending', label: 'Pending', count: stats?.pending },
    { id: 'approved', label: 'Approved', count: stats?.approved },
    { id: 'rejected', label: 'Rejected', count: stats?.rejected },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Instagram className="h-6 w-6" /> Instagram Reels
          </h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Curate what the storefront shows. Visitor submissions land here as Pending — nothing
            appears on your site until you approve it.
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> Add reel
        </Button>
      </div>

      {stats && stats.pending > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          {stats.pending} submission{stats.pending === 1 ? '' : 's'} waiting for your review.
        </div>
      )}

      <div className="flex gap-2">
        {filters.map((f) => (
          <Button
            key={f.id}
            size="sm"
            variant={statusFilter === f.id ? 'default' : 'outline'}
            onClick={() => setStatusFilter(f.id)}
          >
            {f.label}
            {typeof f.count === 'number' && (
              <span className="ml-1.5 rounded-full bg-black/10 px-1.5 text-xs">{f.count}</span>
            )}
          </Button>
        ))}
      </div>

      {listQuery.isPending ? (
        <p className="text-muted-foreground py-12 text-center text-sm">Loading reels…</p>
      ) : reels.length === 0 ? (
        <Card>
          <CardContent className="text-muted-foreground py-12 text-center text-sm">
            No reels here yet. Add one with its Instagram link — it takes ten seconds.
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {reels.map((reel, index) => (
            <Card key={reel._id} className="overflow-hidden">
              <div className="flex gap-3 p-3">
                {/* IG embeds lay out for ~330px width; render at 330 and
                    scale into the 112×176 thumbnail so nothing overlaps. */}
                <div className="bg-muted relative h-44 w-28 shrink-0 overflow-hidden rounded-md">
                  <iframe
                    src={reel.embedUrl}
                    title={reel.caption || reel.reelId}
                    className="absolute left-0 top-0 border-0"
                    style={{
                      width: 330,
                      height: 176 / (112 / 330),
                      transform: `scale(${112 / 330})`,
                      transformOrigin: 'top left',
                    }}
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                  />
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge className={STATUS_BADGE[reel.status].className}>
                      {STATUS_BADGE[reel.status].label}
                    </Badge>
                    <Badge variant="outline">
                      {reel.source === 'submission'
                        ? 'Visitor submission'
                        : reel.source === 'channel'
                          ? 'Channel sync'
                          : 'Curated'}
                    </Badge>
                    {reel.authorHandle && (
                      <Badge variant="outline" title="Poster read from Instagram's embed page">
                        by @{reel.authorHandle}
                      </Badge>
                    )}
                  </div>
                  {reel.caption && <p className="line-clamp-2 text-sm">{reel.caption}</p>}
                  {reel.credit && <p className="text-muted-foreground text-xs">@{reel.credit}</p>}
                  <a
                    href={reel.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                  >
                    Open on Instagram <ExternalLink className="h-3 w-3" />
                  </a>

                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <Button
                      size="sm"
                      variant={reel.placements.includes('home') ? 'default' : 'outline'}
                      className="h-7 px-2 text-xs"
                      onClick={() => togglePlacement(reel, 'home')}
                      title="Show on the home-page reel rail"
                    >
                      <Home className="mr-1 h-3 w-3" /> Home
                    </Button>
                    <Button
                      size="sm"
                      variant={reel.placements.includes('product') ? 'default' : 'outline'}
                      className="h-7 px-2 text-xs"
                      onClick={() => togglePlacement(reel, 'product')}
                      title="Show on the linked product pages"
                    >
                      <ShoppingBag className="mr-1 h-3 w-3" /> Products
                      {reel.productIds.length > 0 && ` (${reel.productIds.length})`}
                    </Button>
                  </div>

                  {reel.placements.includes('product') && (
                    <MultiSelectPicker
                      id={`reel-products-${reel._id}`}
                      label="Linked products"
                      items={productsQuery.data || []}
                      value={reel.productIds}
                      onChange={(productIds) =>
                        updateMutation.mutate({ id: reel._id, data: { productIds } })
                      }
                      placeholder="Search by name or SKU…"
                    />
                  )}
                </div>
              </div>

              <div className="bg-muted/40 flex items-center justify-between border-t px-3 py-2">
                <div className="flex gap-1">
                  {reel.status !== 'approved' && (
                    <Button
                      size="sm"
                      className="h-7 bg-emerald-600 px-2 text-xs hover:bg-emerald-700"
                      onClick={() =>
                        updateMutation.mutate({ id: reel._id, data: { status: 'approved' } })
                      }
                    >
                      <Check className="mr-1 h-3 w-3" /> Approve
                    </Button>
                  )}
                  {reel.status !== 'rejected' && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 px-2 text-xs"
                      onClick={() =>
                        updateMutation.mutate({ id: reel._id, data: { status: 'rejected' } })
                      }
                    >
                      <X className="mr-1 h-3 w-3" /> Reject
                    </Button>
                  )}
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                    title="Show earlier"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0"
                    disabled={index === reels.length - 1}
                    onClick={() => move(index, 1)}
                    title="Show later"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 text-red-600"
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Remove this reel?',
                        message: 'It disappears from the storefront immediately.',
                        danger: true,
                      });
                      if (ok) deleteMutation.mutate(reel._id);
                    }}
                    title="Remove"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add-by-URL dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add a reel</DialogTitle>
            <DialogDescription>
              Paste the reel&apos;s link from Instagram&apos;s share menu. Reels you add yourself go
              live immediately (you are the moderator).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reel-url">Instagram link(s)</Label>
              <Textarea
                id="reel-url"
                rows={3}
                placeholder={
                  'https://www.instagram.com/reel/…\nPaste several links (one per line) to add them all at once — e.g. from your Tagged tab.'
                }
                value={draft.url}
                onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="reel-caption">Caption (optional)</Label>
              <Textarea
                id="reel-caption"
                rows={2}
                placeholder="Shown if Instagram's embed can't load"
                value={draft.caption}
                onChange={(e) => setDraft((d) => ({ ...d, caption: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="reel-credit">Credit handle (optional)</Label>
              <Input
                id="reel-credit"
                placeholder="@customer_handle — for featured customer reels"
                value={draft.credit}
                onChange={(e) => setDraft((d) => ({ ...d, credit: e.target.value }))}
              />
            </div>
            <div>
              <Label>Where should it appear?</Label>
              <div className="mt-1.5 flex gap-2">
                {(['home', 'product'] as const).map((pl) => (
                  <Button
                    key={pl}
                    type="button"
                    size="sm"
                    variant={draftPlacements.includes(pl) ? 'default' : 'outline'}
                    onClick={() =>
                      setDraftPlacements((cur) =>
                        cur.includes(pl) ? cur.filter((x) => x !== pl) : [...cur, pl]
                      )
                    }
                  >
                    {pl === 'home' ? 'Home page' : 'Product pages'}
                  </Button>
                ))}
              </div>
            </div>
            {draftPlacements.includes('product') && (
              <MultiSelectPicker
                id="reel-draft-products"
                label="Linked products"
                items={productsQuery.data || []}
                value={draftProductIds}
                onChange={setDraftProductIds}
                placeholder="Search by name or SKU…"
              />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!draft.url.trim() || createMutation.isPending}
            >
              {createMutation.isPending ? 'Adding…' : 'Add reel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
